import { Field, FieldProps, Form, useFormikContext } from 'formik';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  BodyText,
  Button,
  Grid,
  ISelectOption,
  ISelectProps,
  NumberField,
  Select,
  SelectField,
  SpacerRow,
  TextField,
} from 'ui-toolkit';
import { IOrderOption } from '../../types/accounting/IOrderOption';
import {
  categoriesRequiringMemo,
  eInternalTransferCategory,
  internalTransferCategoryOptions,
} from '../../types/accounting/response/eInternalTransferCategory';
import { OrderSelectHintText } from './OrderSelectHintText';
import { IUnrestrictedInternalTransferCreateData } from '../../types/accounting/data/IUnrestrictedInternalTransferCreateData';
import { IEscrowFileData } from '../../types/accounting/data/IEscrowFileData';
import { OrderSelectField } from '../shared/OrderSelect/OrderSelectField';
import { ISearchOrderRequest } from '../../ui-toolkit/orders/searchOrder/types/ISearchOrder';

const LargerGapGrid = styled(Grid)`
  grid-template-columns: 1fr max-content max-content;
  justify-items: end;
`;

const OrderAssociationItems = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
  grid-template-columns: 36px 1fr;
  grid-column: span 2;
  text-align: left;
`;

const CircledLetterWrapper = styled.div<{ isLast?: boolean }>`
  display: grid;
  grid-template:
    'circle'
    'line' 1fr;
  justify-items: center;
  position: relative;

  ${(props) =>
    !props.isLast
      ? `
        :after {
          background-color: ${props.theme.colors.gray};
          bottom: -8px;
          content: '';
          display: block;
          height: 50%;
          left: calc(50% - 1px);
          position: absolute;
          width: 2px;
        }
        `
      : ''}
`;

const CircledLetter = styled(BodyText)<{ isValid: boolean; isError: boolean }>`
  align-items: center;
  background-color: ${(props) =>
    props.isError
      ? props.theme.colors.error
      : props.isValid
        ? props.theme.colors.primary
        : props.theme.colors.darkGray};
  border-radius: 16px;
  color: ${(props) => props.theme.colors.white};
  display: flex;
  justify-content: center;
  position: relative;
  height: 32px;
  width: 32px;
`;

const AccountField: React.FC<FieldProps & ISelectProps & { isLast: boolean; letter: string }> = ({
  field,
  form,
  letter,
  isLast,
  ...rest
}) => {
  const hasError = (!!form.submitCount || !!form.touched[field.name]) && !!form.errors[field.name];
  const onChange = (value: any) => {
    form.setFieldTouched(field.name);
    form.setFieldValue(field.name, value ? value.value : undefined);
  };
  const value = (rest.options || []).find((o: any) => o.value === field.value);

  return (
    <>
      <CircledLetterWrapper isLast={isLast}>
        <CircledLetter isError={hasError} isValid={!!form.submitCount && !hasError}>
          {letter}
        </CircledLetter>
      </CircledLetterWrapper>
      <Select {...field} {...rest} onChange={onChange} errorText={hasError} value={value} />
    </>
  );
};

//TODO: export HintText from UI Toolkit's FormCell so it can be used instead of this duplicate
const TwoColumnHintText = styled.div`
  ${(props) => props.theme.fonts.mixins.tiny};
  color: ${(props) => props.theme.colors.contentPlaceholder};
  grid-column: span 2;
`;

export interface IUnrestrictedInternalTransfersFormProps {
  destinationAccounts: ISelectOption<string>[];
  escrowFile: IEscrowFileData | undefined;
  onCancel: () => void;
  onOrderSelect: Dispatch<SetStateAction<ISearchOrderRequest | undefined>>;
  sourceAccounts: ISelectOption<string>[];
}

export const UnrestrictedInternalTransfersForm: React.FC<IUnrestrictedInternalTransfersFormProps> = ({
  destinationAccounts,
  escrowFile,
  onCancel,
  onOrderSelect,
  sourceAccounts,
}) => {
  const {
    values: { category, note },
    setFieldValue,
  } = useFormikContext<IUnrestrictedInternalTransferCreateData>();
  const handleCategoryChange = useCallback(
    (selectedCategory: any) => {
      setFieldValue('category', selectedCategory);
    },
    [setFieldValue] // Dependencies
  );
  const [selectedOrderOption, setSelectedOrderOption] = useState<IOrderOption>();
  const showMemoText = useMemo(() => !!selectedOrderOption && !!category, [selectedOrderOption, category]);

  const memoText = useMemo(() => {
    if (!selectedOrderOption || !category) return '';
    const fileNumber = selectedOrderOption.data.fileNumber;
    const categoryText = category.toUpperCase();
    const noteText = category === eInternalTransferCategory.other ? `-${note || ''}` : '';
    return `IT-${categoryText}-${fileNumber}${noteText}`;
  }, [selectedOrderOption, category, note]);

  const isNoteFieldRequired = useMemo(() => {
    return !!category && categoriesRequiringMemo.includes(category);
  }, [category]);
  const noteLabel = useMemo(() => (category === eInternalTransferCategory.other ? 'Note' : 'Memo'), [category]);
  const notePlaceholder = useMemo(() => {
    return category === eInternalTransferCategory.other ? undefined : 'Enter WF Transaction Number';
  }, [category]);

  useEffect(() => {
    setFieldValue('escrowFileBankAccountId', escrowFile ? escrowFile.depositEscrowBankAccount.id : null);
  }, [setFieldValue, escrowFile]);

  return (
    <Form>
      <Grid columnCount={2}>
        <OrderSelectField
          name="orderId"
          label="File Number"
          isRequired
          placeholder="Enter File Number"
          inputId="orderId"
          isClearable
          hintText={
            selectedOrderOption && <OrderSelectHintText selectedOption={selectedOrderOption} escrowFile={escrowFile} />
          }
          onOptionSet={(orderOption?: IOrderOption) => {
            if (!selectedOrderOption || !orderOption || selectedOrderOption.value !== orderOption.value) {
              setSelectedOrderOption(orderOption);
              onOrderSelect(!!orderOption ? orderOption.data : undefined);
            }
          }}
        />
        <NumberField name="amountCents" prefix="$" mode="currency" label="Amount" valueIsCents={true} />
        <OrderAssociationItems>
          <Field
            name="sourceAccount"
            component={AccountField}
            label="Source Account"
            letter="A"
            placeholder="Select A Source Account"
            options={sourceAccounts}
            menuPosition="fixed"
          />
          <Field
            name="destinationAccount"
            component={AccountField}
            label="Destination Account"
            letter="B"
            isLast={true}
            placeholder="Select A Destination Account"
            options={destinationAccounts}
            menuPosition="fixed"
          />
        </OrderAssociationItems>
      </Grid>
      <SpacerRow />
      <Grid columnCount={2}>
        <SelectField
          name="category"
          menuPosition="fixed"
          label="Category"
          placeholder="Select A Category"
          options={internalTransferCategoryOptions}
          onChange={handleCategoryChange}
        />
        {isNoteFieldRequired && <TextField name="note" label={noteLabel} placeholder={notePlaceholder} />}
      </Grid>

      {showMemoText && (
        <TwoColumnHintText>
          FYI: The Memo for this Internal Transfer will be <code>{memoText}</code>
        </TwoColumnHintText>
      )}

      <SpacerRow />
      <LargerGapGrid templateColumns="max-content max-content">
        <Button intent="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" intent="primary">
          Save and Add
        </Button>
      </LargerGapGrid>
    </Form>
  );
};
